/* tslint:disable */
import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpRequest,
  HttpResponse,
  HttpHeaders,
} from '@angular/common/http';
import { BaseService as __BaseService } from '../base-service';
import { ApiConfiguration as __Configuration } from '../api-configuration';
import { StrictHttpResponse as __StrictHttpResponse } from '../strict-http-response';
import { Observable as __Observable } from 'rxjs';
import { map as __map, filter as __filter } from 'rxjs/operators';

import { GCPCluster } from '../models/gcpcluster';
import { GCPClusterRequest } from '../models/gcpcluster-request';

/**
 * Ui GCP Cluster Controller
 */
@Injectable({
  providedIn: 'root',
})
class UiGcpClusterControllerService extends __BaseService {
  static readonly createGCPClusterUsingPOSTPath = '/cc-ui/v1/gcp/clusters';
  static readonly configureDraftClusterUsingPOST2Path =
    '/cc-ui/v1/gcp/clusters/configure/{clusterId}';
  static readonly validateVpcIdUsingGET1Path =
    '/cc-ui/v1/gcp/clusters/validate-vpcId';
  static readonly getGCPClusterUsingGETPath =
    '/cc-ui/v1/gcp/clusters/{clusterId}';
  static readonly updateGCPClusterUsingPUTPath =
    '/cc-ui/v1/gcp/clusters/{clusterId}';

  constructor(config: __Configuration, http: HttpClient) {
    super(config, http);
  }

  /**
   * createGCPCluster
   * @param request request
   * @return OK
   */
  createGCPClusterUsingPOSTResponse(
    request: GCPClusterRequest,
  ): __Observable<__StrictHttpResponse<GCPCluster>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    __body = request;
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/cc-ui/v1/gcp/clusters`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<GCPCluster>;
      }),
    );
  }
  /**
   * createGCPCluster
   * @param request request
   * @return OK
   */
  createGCPClusterUsingPOST(
    request: GCPClusterRequest,
  ): __Observable<GCPCluster> {
    return this.createGCPClusterUsingPOSTResponse(request).pipe(
      __map((_r) => _r.body as GCPCluster),
    );
  }

  /**
   * configureDraftCluster
   * @param params The `UiGcpClusterControllerService.ConfigureDraftClusterUsingPOST2Params` containing the following parameters:
   *
   * - `request`: request
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  configureDraftClusterUsingPOST2Response(
    params: UiGcpClusterControllerService.ConfigureDraftClusterUsingPOST2Params,
  ): __Observable<__StrictHttpResponse<GCPCluster>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    __body = params.request;

    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl +
        `/cc-ui/v1/gcp/clusters/configure/${encodeURIComponent(
          String(params.clusterId),
        )}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<GCPCluster>;
      }),
    );
  }
  /**
   * configureDraftCluster
   * @param params The `UiGcpClusterControllerService.ConfigureDraftClusterUsingPOST2Params` containing the following parameters:
   *
   * - `request`: request
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  configureDraftClusterUsingPOST2(
    params: UiGcpClusterControllerService.ConfigureDraftClusterUsingPOST2Params,
  ): __Observable<GCPCluster> {
    return this.configureDraftClusterUsingPOST2Response(params).pipe(
      __map((_r) => _r.body as GCPCluster),
    );
  }

  /**
   * validateVpcId
   * @param params The `UiGcpClusterControllerService.ValidateVpcIdUsingGET1Params` containing the following parameters:
   *
   * - `vpcId`: vpcId
   *
   * - `accountId`: accountId
   *
   * @return OK
   */
  validateVpcIdUsingGET1Response(
    params: UiGcpClusterControllerService.ValidateVpcIdUsingGET1Params,
  ): __Observable<__StrictHttpResponse<boolean>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    if (params.vpcId != null)
      __params = __params.set('vpcId', params.vpcId.toString());
    if (params.accountId != null)
      __params = __params.set('accountId', params.accountId.toString());
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/cc-ui/v1/gcp/clusters/validate-vpcId`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'text',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return (_r as HttpResponse<any>).clone({
          body: (_r as HttpResponse<any>).body === 'true',
        }) as __StrictHttpResponse<boolean>;
      }),
    );
  }
  /**
   * validateVpcId
   * @param params The `UiGcpClusterControllerService.ValidateVpcIdUsingGET1Params` containing the following parameters:
   *
   * - `vpcId`: vpcId
   *
   * - `accountId`: accountId
   *
   * @return OK
   */
  validateVpcIdUsingGET1(
    params: UiGcpClusterControllerService.ValidateVpcIdUsingGET1Params,
  ): __Observable<boolean> {
    return this.validateVpcIdUsingGET1Response(params).pipe(
      __map((_r) => _r.body as boolean),
    );
  }

  /**
   * getGCPCluster
   * @param clusterId clusterId
   * @return OK
   */
  getGCPClusterUsingGETResponse(
    clusterId: string,
  ): __Observable<__StrictHttpResponse<GCPCluster>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl +
        `/cc-ui/v1/gcp/clusters/${encodeURIComponent(String(clusterId))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<GCPCluster>;
      }),
    );
  }
  /**
   * getGCPCluster
   * @param clusterId clusterId
   * @return OK
   */
  getGCPClusterUsingGET(clusterId: string): __Observable<GCPCluster> {
    return this.getGCPClusterUsingGETResponse(clusterId).pipe(
      __map((_r) => _r.body as GCPCluster),
    );
  }

  /**
   * updateGCPCluster
   * @param params The `UiGcpClusterControllerService.UpdateGCPClusterUsingPUTParams` containing the following parameters:
   *
   * - `request`: request
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  updateGCPClusterUsingPUTResponse(
    params: UiGcpClusterControllerService.UpdateGCPClusterUsingPUTParams,
  ): __Observable<__StrictHttpResponse<GCPCluster>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    __body = params.request;

    let req = new HttpRequest<any>(
      'PUT',
      this.rootUrl +
        `/cc-ui/v1/gcp/clusters/${encodeURIComponent(
          String(params.clusterId),
        )}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json',
      },
    );

    return this.http.request<any>(req).pipe(
      __filter((_r) => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<GCPCluster>;
      }),
    );
  }
  /**
   * updateGCPCluster
   * @param params The `UiGcpClusterControllerService.UpdateGCPClusterUsingPUTParams` containing the following parameters:
   *
   * - `request`: request
   *
   * - `clusterId`: clusterId
   *
   * @return OK
   */
  updateGCPClusterUsingPUT(
    params: UiGcpClusterControllerService.UpdateGCPClusterUsingPUTParams,
  ): __Observable<GCPCluster> {
    return this.updateGCPClusterUsingPUTResponse(params).pipe(
      __map((_r) => _r.body as GCPCluster),
    );
  }
}

module UiGcpClusterControllerService {
  /**
   * Parameters for configureDraftClusterUsingPOST2
   */
  export interface ConfigureDraftClusterUsingPOST2Params {
    /**
     * request
     */
    request: GCPClusterRequest;

    /**
     * clusterId
     */
    clusterId: string;
  }

  /**
   * Parameters for validateVpcIdUsingGET1
   */
  export interface ValidateVpcIdUsingGET1Params {
    /**
     * vpcId
     */
    vpcId: string;

    /**
     * accountId
     */
    accountId: string;
  }

  /**
   * Parameters for updateGCPClusterUsingPUT
   */
  export interface UpdateGCPClusterUsingPUTParams {
    /**
     * request
     */
    request: GCPClusterRequest;

    /**
     * clusterId
     */
    clusterId: string;
  }
}

export { UiGcpClusterControllerService };
